import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { Tag } from 'lucide-react'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { BoolCell, DateCell, ListCell, MutedCell, NameCell } from '@/components/TableCells'
import { AdUnit } from '@/services/adUnits/adUnitsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  requestedInLastDay: 'requestedInLastDay',
  objectState: 'objectState',
  site: 'site.name',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface AdUnitTableProps {
  adUnits: AdUnit[]
  loading?: boolean
  emptyMessage?: string
  selection?: AdUnit[]
  onSelectionChange?: (adUnits: AdUnit[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function AdUnitTable({
  adUnits,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: AdUnitTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns = useMemo<ColumnDef<AdUnit, unknown>[]>(() => [
    ...(selectable ? [selectionColumn<AdUnit>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'tagId',
      header: t('adUnits.tagId'),
      meta: { minWidth: 140 },
      cell: ({ row }) => (
        <span className="chip chip--tag" style={{ fontSize: 11 }}>
          <Tag className="inline-block h-3 w-3" />
          {row.original.tagId}
        </span>
      ),
    },
    {
      accessorKey: 'requestedInLastDay',
      header: t('adUnits.requested'),
      size: 110,
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: row.original.requestedInLastDay ? 'var(--ad-accent)' : 'var(--ad-text-muted)' }}>
          {row.original.requestedInLastDay ? t('common.yes') : t('common.no')}
        </span>
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    },
    optionalColumn<AdUnit>({
      id: 'site',
      header: t('adUnits.site'),
      accessorFn: (row) => row.site?.name ?? '',
      meta: { minWidth: 150 },
      cell: ({ row }) => <MutedCell value={row.original.site?.name} />,
    }),
    optionalColumn<AdUnit>({
      accessorKey: 'earningsAccountName',
      header: t('sites.publisher'),
      meta: { minWidth: 150 },
      cell: ({ row }) => <MutedCell value={row.original.earningsAccountName} />,
    }),
    optionalColumn<AdUnit>({
      id: 'dimensions',
      header: t('adUnits.dimensions'),
      enableSorting: false,
      meta: { minWidth: 140 },
      cell: ({ row }) => <ListCell values={row.original.dimensions?.map(([w, h]) => `${w}×${h}`)} />,
    }),
    optionalColumn<AdUnit>({
      accessorKey: 'paywalled',
      header: t('common.paywalled'),
      size: 110,
      cell: ({ row }) => <BoolCell value={row.original.paywalled} />,
    }),
    optionalColumn<AdUnit>({
      id: 'teams',
      header: t('common.teams'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.teams?.map((team) => team.name)} />,
    }),
    optionalColumn<AdUnit>({
      id: 'labels',
      header: t('common.labels'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.labels} />,
    }),
    optionalColumn<AdUnit>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<AdUnit>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ], [t, selectable])

  return (
    <DataTable
      columns={columns}
      data={adUnits}
      loading={loading}
      emptyMessage={emptyMessage ?? 'No ad units found.'}
      columnsKey="adUnits"
      onRowClick={(row) => navigate(`/publishing/ad-units/${row.id}`)}
      selection={selection}
      onSelectionChange={onSelectionChange}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
